<template>
  <div class="container">
    <a href="https://integris-mgt.com">
      <img src="https://integris-mgt.com/assets/img/logo/logo.svg" class="logo" /></a>
    <div class="card main-card no-print-border">
      <div class="card-header border-0">
        <div class="d-flex justify-content-between align-items-center">
          <h1>{{ translations[selectedLanguage].title }}</h1>
          <div class="language-selector">
            <div class="d-flex align-items-center">
              <label for="languageSelect" class="me-2">Language:</label>
              <select v-model="selectedLanguage" id="languageSelect" class="form-select">
                <option value="en">English</option>
                <option value="fr">Français</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body">
        <form class="row" @submit.prevent>
          <div class="col-lg-6 d-print-none">
            <!-- Progress Indicator -->
            <div class="progress-section mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <small class="text-muted">{{ translations[selectedLanguage].formProgress }}</small>
                <small class="fw-bold" :class="progressPercentage === 100 ? 'text-success' : 'text-primary'">{{ progressPercentage }}%</small>
              </div>
              <div class="progress" style="height: 6px;">
                <div class="progress-bar" :class="progressPercentage === 100 ? 'bg-success' : ''" role="progressbar" :style="{ width: progressPercentage + '%' }" :aria-valuenow="progressPercentage" aria-valuemin="0" aria-valuemax="100"></div>
              </div>
            </div>

            <!-- Member Information Section -->
            <div class="form-section mb-4">
              <h6 class="section-title">
                {{ translations[selectedLanguage].memberInfo }}
                <span v-if="isMemberInfoComplete" class="section-check text-success ms-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                  </svg>
                </span>
              </h6>
              <div class="mb-3">
                <label for="planRegistrationNo" class="form-label">{{ translations[selectedLanguage].planRegistrationNo }}
                  <span class="text-danger">*</span></label>
                <div class="input-group">
                <input type="text" class="form-control" :class="{ 'is-valid': planRegistrationNo }" id="planRegistrationNo" v-model="planRegistrationNo" 
                  aria-describedby="planRegistrationNoHelp" />
                <span v-if="planRegistrationNo" class="input-group-text bg-success text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                  </svg>
                </span>
              </div>
              </div>
              <div class="mb-3">
                <label for="memberName" class="form-label">{{ translations[selectedLanguage].memberName }} <span
                    class="text-danger">*</span></label>
                <div class="input-group">
                  <select v-if="memberNamesList.length > 0" class="form-select" :class="{ 'is-valid': memberName }" id="memberName" v-model="memberName">
                    <option value="">{{ translations[selectedLanguage].choose }}</option>
                    <option v-for="name in memberNamesList" :key="name" :value="name">{{ name }}</option>
                  </select>
                  <input v-else type="text" class="form-control" :class="{ 'is-valid': memberName }" id="memberName" v-model="memberName" />
                  <span v-if="memberName" class="input-group-text bg-success text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                    </svg>
                  </span>
                </div>
              </div>
              <div class="row">
                <div class="col-8">
                  <div class="mb-3">
                    <label for="provisionElected" class="form-label">{{ translations[selectedLanguage].provisionElected }}
                      <span class="text-danger">*</span></label>
                    <select class="form-select" :class="{ 'is-valid': provisionElected }" id="provisionElected" v-model="provisionElected">
                      <option disabled value="">{{ translations[selectedLanguage].choose }}</option>
                      <option value="DB">{{ translations[selectedLanguage].definedBenefit }}</option>
                      <option value="DC">{{ translations[selectedLanguage].definedContribution }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-4">
                  <div class="mb-3">
                    <label class="form-label" for="yearSelect">{{ translations[selectedLanguage].year }}</label>
                    <select v-model="selectedYear" id="yearSelect" class="form-select">
                      <option v-for="year in yearsAvailable" :key="year" :value="year" :disabled="isYearDisabled(year)">
                        {{ year }}{{ isYearDisabled(year) ? ' (' + translations[selectedLanguage].availableDec + ')' : '' }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Contribution Details Section -->
            <div class="form-section">
              <h6 class="section-title">
                {{ translations[selectedLanguage].contributionDetails }}
                <span v-if="isContributionDetailsComplete" class="section-check text-success ms-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                  </svg>
                </span>
              </h6>
              <div class="mb-3">
                <label for="t4Income" class="form-label">{{ translations[selectedLanguage].t4Income }} {{ selectedYear }}
                  <span class="text-danger">*</span>
                  <button type="button" class="btn btn-link btn-sm p-0 ms-1 info-toggle" @click="toggleHelp('t4Income')" :aria-expanded="showHelp.t4Income">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                      <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/>
                    </svg>
                  </button>
                </label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="text" class="form-control" :class="{ 'is-valid': t4Income > 0 }" id="t4Income" 
                    :value="formatInputDisplay(t4Income)" @input="updateT4Income($event)" @blur="formatOnBlur('t4Income')"
                    inputmode="decimal" aria-describedby="t4IncomeBlock" />
                  <span v-if="t4Income > 0" class="input-group-text bg-success text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                    </svg>
                  </span>
                </div>
                <div id="t4IncomeBlock" class="form-text help-text" :class="{ 'show': showHelp.t4Income }">
                  {{ translations[selectedLanguage].t4IncomeBlock }}
                </div>
              </div>

              <div class="mb-3" v-show="isDC">
                <label for="employerContributions" class="form-label">{{
                  translations[selectedLanguage].employerContributions }} <span class="text-danger">*</span>
                  <button type="button" class="btn btn-link btn-sm p-0 ms-1 info-toggle" @click="toggleHelp('employer')" :aria-expanded="showHelp.employer">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                      <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/>
                    </svg>
                  </button>
                </label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="text" class="form-control" :class="{ 'is-valid': employerContributions > 0 }" id="employerContributions" 
                    :value="formatInputDisplay(employerContributions)" @input="updateEmployerContributions($event)" @blur="formatOnBlur('employerContributions')"
                    inputmode="decimal" aria-describedby="employerContributionsBlock" />
                  <span v-if="employerContributions > 0" class="input-group-text bg-success text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                    </svg>
                  </span>
                </div>
                <div id="employerContributionsBlock" class="form-text help-text" :class="{ 'show': showHelp.employer }">
                  {{ translations[selectedLanguage].employerContributionsDescription }} {{ selectedYear + 1 }} {{ translations[selectedLanguage].for }} {{ selectedYear }}.
                </div>
                <div class="alert alert-danger mt-2" role="alert" v-show="showErrorEmployerContributions">
                  {{ translations[selectedLanguage].exceedsMaximum }} {{ formatCurrency(maxContributionsDC) }}
                </div>
              </div>

              <div class="mb-3">
                <label for="vcAccountContributions" class="form-label">{{
                  translations[selectedLanguage].vcAccountContributions }} <span class="text-danger">*</span>
                  <button type="button" class="btn btn-link btn-sm p-0 ms-1 info-toggle" @click="toggleHelp('vc')" :aria-expanded="showHelp.vc">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                      <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/>
                    </svg>
                  </button>
                </label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="text" class="form-control" :class="{ 'is-valid': vcAccountContributions > 0 }" id="vcAccountContributions" 
                    :value="formatInputDisplay(vcAccountContributions)" @input="updateVcContributions($event)" @blur="formatOnBlur('vcAccountContributions')"
                    inputmode="decimal" aria-describedby="vcAccountContributionsBlock" />
                  <span v-if="vcAccountContributions > 0" class="input-group-text bg-success text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                    </svg>
                  </span>
                </div>
                <div id="vcAccountContributionsBlock" class="form-text help-text" :class="{ 'show': showHelp.vc }">
                  {{ translations[selectedLanguage].vcAccountContributionsDescription }} {{ selectedYear + 1 }} {{ translations[selectedLanguage].for }} {{ selectedYear }}.<br>
                  <span v-if="isDB">{{ translations[selectedLanguage].vcAccountContributionsBlockDB }}</span>
                  <span v-if="isDC">{{ translations[selectedLanguage].vcAccountContributionsBlockDC }} {{ formatCurrency(maxContributionByIncome) }} {{ translations[selectedLanguage].ofT4IncomeAnd }} {{ formatCurrency(currentLimits.DC) }}.</span>
                </div>
                <div class="alert alert-danger mt-2" role="alert" v-show="showErrorVcAccountContributionMax">
                  {{ translations[selectedLanguage].vcContributionsMustNotExceed }} {{
                    formatCurrency(vcAccountContributionMax) }}
                </div>
              </div>

              <!-- Reset Button -->
              <button type="button" class="btn btn-outline-secondary btn-sm" @click="resetForm">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="me-1" viewBox="0 0 16 16">
                  <path fill-rule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                  <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                </svg>
                {{ translations[selectedLanguage].reset }}
              </button>
            </div>
          </div>

          <div class="col-lg-6 pt-4" id="printable">
            <h2>{{ t4Header }}</h2>

            <div class="text-muted" v-show="!isFilledOut">
              {{ translations[selectedLanguage].pleaseFillOutAllRequiredFields }}
              <hr />
            </div>

            <div class="alert alert-danger" role="alert" v-show="isErrors">
              {{ translations[selectedLanguage].pleaseCorrectAllErrorsFirst }}
            </div>

            <!-- Success indicator -->
            <!-- <div class="alert alert-success d-flex align-items-center d-print-none" role="alert" v-show="isFilledOut && !isErrors">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="me-2" viewBox="0 0 16 16">
                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
              </svg>
              {{ translations[selectedLanguage].formComplete }}
            </div> -->

            <div class="card card-body summary-card" v-show="isFilledOut && !isErrors">
              <div class="mb-3">
                <span>Box 50: {{ translations[selectedLanguage].rppRegistrationNumber }}: </span>
                <span class="result-value" v-text="planRegistrationNo"></span>
              </div>
              <div class="mb-3">
                <span>Box 20: {{ translations[selectedLanguage].rppVoluntaryContributions }}: </span>
                <span class="result-value" v-text="formatCurrency(rppVoluntaryContributions)"></span>
              </div>
              <div class="mb-3">
                <span>Box 52: {{ translations[selectedLanguage].pensionAdjustment }}: </span>
                <span class="result-value" v-text="formatCurrency(pensionAdjustment, true)"></span>
              </div>

              <hr class="d-print-none" />
              <div class="d-flex gap-2 d-print-none">
                <button class="btn btn-primary" @click="printDiv('printable')">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="me-1" viewBox="0 0 16 16">
                    <path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                    <path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2H5zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4V3zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2H5zm7 2v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1z"/>
                  </svg>
                  {{ translations[selectedLanguage].print }}
                </button>
                <button class="btn btn-outline-secondary" @click="copyResults" :class="{ 'btn-success': copied }">
                  <svg v-if="!copied" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="me-1" viewBox="0 0 16 16">
                    <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                    <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="me-1" viewBox="0 0 16 16">
                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                  </svg>
                  {{ copied ? translations[selectedLanguage].copied : translations[selectedLanguage].copy }}
                </button>
              </div>
            </div>

            <div class="text-muted pt-2">
              {{ translations[selectedLanguage].ifMemberHasCeasedMembership }} <a href="mailto:actuarial@integris-mgt.com" class="">actuarial@integris-mgt.com</a>
            </div>
            <div class="text-muted pt-2 d-print-none">
              <small>{{ translations[selectedLanguage].dbLimit }}: {{ formatCurrency(currentLimits.DB) }}, {{
                translations[selectedLanguage].dcLimit }}: {{ formatCurrency(currentLimits.DC) }}</small>
            </div>
          </div>
        </form>
      </div>
    </div>
    <footer class="footer-styled">
      <div class="footer-content">
        <span>{{ translations[selectedLanguage].footer }}</span>
        <a href="mailto:actuarial@integris-mgt.com" class="footer-link">actuarial@integris-mgt.com</a>
      </div>
      <div class="footer-copyright">&copy; INTEGRIS Pension Management Corp.</div>
    </footer>
  </div>
</template>
<script>
export default {
  name: "App",
  components: {},
  data() {
    return {
      limits: [
        { year: 2023, DB: 3506.67, DC: 31560 },
        { year: 2024, DB: 3610, DC: 32490 },
        { year: 2025, DB: 3756.67, DC: 33810 },
        { year: 2026, DB: 3932.22, DC: 35390 }
      ],
      selectedYear: 2025,
      vcAccountContributionMax: 600,
      t4Income: 0,
      memberName: "",
      memberNamesList: [],
      planRegistrationNo: "",
      provisionElected: "",
      vcAccountContributions: 0,
      employerContributions: 0,
      selectedLanguage: 'en',
      copied: false,
      showHelp: {
        t4Income: true,
        employer: true,
        vc: true
      },
      translations: {
        en: {
          title: 'Pension Adjustment Calculation Tool',
          planRegistrationNo: 'Plan Registration No.',
          memberName: 'Member Name',
          year: 'Year',
          provisionElected: 'Provision Elected',
          t4info: 'Information to report on',
          footer: 'Have a question? Please contact',
          choose: 'Choose...',
          definedBenefit: 'Defined Benefit Provision (DB)',
          definedContribution: 'Defined Contribution Provision (DC)',
          t4Income: 'T4 Income',
          t4IncomeBlock: 'If the member received earnings from more than one participating employer during the year, you can report the Pension Adjustment on one of the participating employer\'s T4 slip. For this purpose, please enter the total T4 income (as reported in Box 14) from all participating employers.',
          employerContributions: 'Employer Contributions to the Member\'s DC Account',
          employerContributionsDescription: 'Enter the amount of Employer contributions made to the DC account or to be made prior to March 1,',
          for: 'for',
          employerContributionsBlock: 'contributions must not exceed the lesser of 18% of T4 Income',
          and: 'and',
          vcAccountContributions: 'Employee Contributions to the Voluntary Contribution (VC) Account',
          vcAccountContributionsDescription: 'Enter the amount of Employee contributions made to the VC Account or to be made prior to March 1,',
          vcAccountContributionsBlockDB: 'The member may contribute up to $600 to the VC Account.',
          vcAccountContributionsBlockDC: 'The combined Employer contributions to the DC Account and Employee contributions to the VC Account must not exceed the lesser of',
          ofT4IncomeAnd: '(18% of T4 income) and',
          vcContributionsMustNotExceed: 'VC Contributions must not exceed',
          noVcContributionsAllowed: 'No VC Contributions Allowed',
          exceedsMaximum: 'Exceeds maximum of',
          pleaseFillOutAllRequiredFields: 'Please fill out all required fields.',
          pleaseCorrectAllErrorsFirst: 'Please correct all errors first before',
          rppRegistrationNumber: 'RPP Registration Number',
          rppVoluntaryContributions: 'RPP (Voluntary) Contributions',
          pensionAdjustment: 'Pension Adjustment',
          print: 'Print',
          copy: 'Copy',
          copied: 'Copied!',
          reset: 'Reset Form',
          memberInfo: 'Member Information',
          contributionDetails: 'Contribution Details',
          formComplete: 'Form complete - results ready',
          formProgress: 'Form Progress',
          availableDec: 'Available Dec 2026',
          ifMemberHasCeasedMembership: '*If the member has ceased membership during the year or joined the Plan part way through the year, please contact',
          dbLimit: 'DB Limit',
          dcLimit: 'DC Limit'
        },
        fr: {
          title: 'Outil de calcul du facteur d\'équivalence',
          planRegistrationNo: 'Numéro d\'enregistrement du régime',
          memberName: 'Nom du membre',
          year: 'Année',
          provisionElected: 'Disposition choisie',
          t4info: 'Informations à déclarer sur le feuillet T4',
          footer: 'Vous avez une question? Veuillez contacter',
          choose: 'Choisir...',
          definedBenefit: 'Disposition de prestation déterminée (PD)',
          definedContribution: 'Disposition de cotisations déterminées (CD)',
          t4Income: 'Revenu T4',
          t4IncomeBlock: 'Si le membre a reçu des revenus de plus d\'un employeur participant au cours de l\'année, vous pouvez déclarer le facteur d\'équivalence sur le feuillet T4 de l\'un des employeurs participants. À cette fin, veuillez saisir le revenu total figurant sur les feuillets T4 (tel qu\'indiqué à la case 14) de tous les employeurs participants.',
          employerContributions: 'Cotisations de l\'employeur au compte CD du membre',
          employerContributionsDescription: 'Entrez le montant des cotisations de l\'employeur versées au compte CD ou à verser avant le 1er mars',
          for: 'pour',
          employerContributionsBlock: 'les cotisations ne doivent pas dépasser le moins élevé de 18 % du revenu T4',
          and: 'et',
          vcAccountContributions: 'Cotisations des employés au compte de cotisations volontaires (CV)',
          vcAccountContributionsDescription: 'Entrez le montant des cotisations des employés versées au compte CV ou à verser avant le 1er mars',
          vcAccountContributionsBlockDB: 'Le membre peut contribuer jusqu\'à 600 $ au compte CV.',
          vcAccountContributionsBlockDC: 'Les cotisations combinées de l\'employeur au compte CD et les cotisations des employés au compte CV ne doivent pas dépasser le moins élevé de',
          ofT4IncomeAnd: '(18 % du revenu T4) et',
          vcContributionsMustNotExceed: 'Les cotisations CV ne doivent pas dépasser',
          noVcContributionsAllowed: 'Aucune cotisation CV n\'est autorisée',
          exceedsMaximum: 'Dépasse le maximum de',
          pleaseFillOutAllRequiredFields: 'Veuillez remplir tous les champs obligatoires.',
          pleaseCorrectAllErrorsFirst: 'Veuillez corriger toutes les erreurs avant',
          rppRegistrationNumber: 'Numéro d\'enregistrement du RPA',
          rppVoluntaryContributions: 'Contributions volontaires du RPA',
          pensionAdjustment: 'Facteur d\'équivalence',
          print: 'Imprimer',
          copy: 'Copier',
          copied: 'Copié!',
          reset: 'Réinitialiser',
          memberInfo: 'Informations sur le membre',
          contributionDetails: 'Détails des cotisations',
          formComplete: 'Formulaire complet - résultats prêts',
          formProgress: 'Progression du formulaire',
          availableDec: 'Disponible déc. 2026',
          ifMemberHasCeasedMembership: '*Si le membre a cessé d\'être membre au cours de l\'année ou a rejoint le régime au cours de l\'année, veuillez contacter',
          dbLimit: 'Limite PD',
          dcLimit: 'Limite CD'
        }
      }
    };
  },
  created() {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    if (langParam && ['en', 'fr'].includes(langParam)) {
      this.selectedLanguage = langParam;
    }
    const urlPlanRegistrationNo = urlParams.get("planRegistrationNo");
    if (urlPlanRegistrationNo) {
      this.planRegistrationNo = urlPlanRegistrationNo;
    }
    this.provisionElected = urlParams.get("provisionElected") || "";
    
    const memberNamesParam = urlParams.get("memberNames");
    if (memberNamesParam) {
      try {
        this.memberNamesList = JSON.parse(decodeURIComponent(memberNamesParam));
      } catch (e) {
        this.memberNamesList = [];
      }
    }
  },
  

  computed: {
    yearsAvailable() {
      return [2023, 2024, 2025, 2026];
    },
    isDB() {
      return this.provisionElected === "DB";
    },
    isDC() {
      return this.provisionElected === "DC";
    },
    isMemberInfoComplete() {
      return this.planRegistrationNo && this.memberName && this.provisionElected;
    },
    isContributionDetailsComplete() {
      return this.t4Income > 0;
    },
    progressPercentage() {
      let completed = 0;
      const total = this.isDC ? 5 : 4; // DC has employer contributions field
      
      if (this.planRegistrationNo) completed++;
      if (this.memberName) completed++;
      if (this.provisionElected) completed++;
      if (this.t4Income > 0) completed++;
      if (this.isDC && this.employerContributions >= 0) completed++;
      
      return Math.round((completed / total) * 100);
    },
    t4Header() {
      if (this.selectedLanguage === 'fr') {
        const name = this.memberName ? 'de ' + this.memberName : 'du membre';
        return this.translations.fr.t4info + ' ' + name;
      } else {
        const name = this.memberName ? this.memberName + "'s" : "member's";
        return this.translations.en.t4info + ' ' + name + ' T4 slip';
      }
    },
    maxContributionByIncome() {
      return 0.18 * this.t4Income;
    },
    isErrors() {
      return (
        this.showErrorVcAccountContributionMax ||
        this.showErrorEmployerContributions
      );
    },
    showErrorVcAccountContributionMax() {
      return (
        this.isDB && this.vcAccountContributions > this.vcAccountContributionMax
      );
    },
    showErrorEmployerContributions() {
      return this.employerContributions > this.maxContributionsDC;
    },
    isFilledOut() {
      const hasValidProvision = this.isDB || this.isDC;
      const hasIncome = this.t4Income > 0;
      const hasPlanRegistrationNo = this.planRegistrationNo != "";
      const hasMemberName = this.memberName != "";
      return hasValidProvision && hasIncome && hasPlanRegistrationNo && hasMemberName;
    },
    maxContributionsDC() {
      return Math.ceil(Math.min(this.currentLimits.DC, this.maxContributionByIncome));
    },
    exceedsMaximumDC() {
      return this.employerContributions > this.maxContributionsDC;
    },
    currentLimits() {
      const limits = this.limits.find(limit => limit.year === this.selectedYear);
      return limits || { DB: 0, DC: 0 }; // Return a default object if not found
    },
    rppVoluntaryContributions() {
      if (this.isDB) {
        return this.vcAccountContributions <= 600
          ? this.vcAccountContributions
          : "Error-VC Contributions Must Not Exceed $600";
      } else {
        return this.vcAccountContributions;
      }
    },
    pensionAdjustment() {
      if (this.isDB) {
        const baseAdjustment =
          Math.min(this.currentLimits.DB, 0.02 * this.t4Income) * 9;
        const adjustment =
          Math.round(baseAdjustment - 600) + Math.round(this.rppVoluntaryContributions);
        return Math.max(adjustment, 0); // Ensures minimum of 0
      } else {
        return Math.max(this.employerContributions + this.vcAccountContributions, 0); // Sum of employer DC and member VC contributions
      }
    },
  },
  methods: {
    formatCurrency(value, rounded) {
      if (rounded) {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      } else {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(value);
      }
    },
    isYearDisabled(year) {
      // 2026 is disabled until December 1, 2026
      if (year === 2026) {
        const now = new Date();
        return now < new Date(2026, 11, 1);
      }
      return false;
    },
    printDiv(divName) {
      var printContents = document.getElementById(divName).innerHTML;
      var originalContents = document.body.innerHTML;
      console.log(printContents, originalContents);
      // document.body.innerHTML = printContents;
      window.print();
      // document.body.innerHTML = originalContents;
    },
    toggleHelp(field) {
      this.showHelp[field] = !this.showHelp[field];
    },
    formatInputDisplay(value) {
      if (value === 0 || value === '' || value === null) return '';
      const num = parseFloat(value);
      if (isNaN(num)) return '';
      return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    parseInputValue(value) {
      const cleaned = value.replace(/[^0-9.]/g, '');
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    },
    updateT4Income(event) {
      this.t4Income = this.parseInputValue(event.target.value);
    },
    updateEmployerContributions(event) {
      this.employerContributions = this.parseInputValue(event.target.value);
    },
    updateVcContributions(event) {
      this.vcAccountContributions = this.parseInputValue(event.target.value);
    },
    formatOnBlur(field) {
      // Force re-render with formatted value on blur
      const temp = this[field];
      this[field] = 0;
      this.$nextTick(() => {
        this[field] = temp;
      });
    },
    resetForm() {
      this.t4Income = 0;
      this.memberName = '';
      this.planRegistrationNo = '';
      this.provisionElected = '';
      this.vcAccountContributions = 0;
      this.employerContributions = 0;
      this.selectedYear = 2025;
      this.showHelp = { t4Income: true, employer: true, vc: true };
      this.$forceUpdate();
    },
    
    copyResults() {
      const results = [
        `Box 50: RPP Registration Number: ${this.planRegistrationNo}`,
        this.isDB ? `Box 20: RPP (Voluntary) Contributions: ${this.formatCurrency(this.rppVoluntaryContributions)}` : null,
        `Box 52: Pension Adjustment: ${this.formatCurrency(this.pensionAdjustment, true)}`
      ].filter(Boolean).join('\n');
      
      navigator.clipboard.writeText(results).then(() => {
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      });
    },
  },
};
</script>

<style>
body {
  background: #eee !important;
}

h1 {
  font-size: 1.35rem !important;
  font-weight: 600 !important;
  line-height: 1.4 !important;
  color: #212529;
}

h2 {
  font-size: 1.1rem !important;
  font-weight: 600 !important;
  line-height: 1.4 !important;
  color: #495057;
  margin-bottom: 1rem;
}

.logo {
  width: 15%;
  margin: 1rem auto;
}

@media (max-width: 991.98px) {
  .logo {
    width: 35%;
    margin: 1rem auto;
  }
}

/* Main card styling */
.main-card {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: none;
  border-radius: 12px;
  overflow: hidden;
}

.main-card .card-header {
  background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
  border-bottom: 3px solid #8bc34a;
  padding: 1.25rem 1.5rem;
}

/* Footer styling */
.footer-styled {
  /* background: #fff; */
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin: 1.5rem 0;
  /* box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05); */
  text-align: center;
}

.footer-content {
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.footer-link {
  color: #0d6efd;
  text-decoration: none;
  margin-left: 0.25rem;
}

.footer-link:hover {
  text-decoration: underline;
}

.footer-copyright {
  font-size: 0.85rem;
  color: #888;
}

/* Progress section */
.progress-section {
  padding: 0.5rem 0;
}

.progress {
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar {
  transition: width 0.4s ease, background-color 0.3s ease;
}

/* Form sections */
.form-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid #e9ecef;
}

.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #dee2e6;
  display: flex;
  align-items: center;
}

.section-check {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
}

/* Valid input styling */
.form-control.is-valid,
.form-select.is-valid {
  border-color: #198754;
  background-image: none;
}

.form-control.is-valid:focus,
.form-select.is-valid:focus {
  border-color: #198754;
  box-shadow: 0 0 0 0.25rem rgba(25, 135, 84, 0.25);
}

/* Collapsible help text */
.help-text {
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition: max-height 0.3s ease, opacity 0.3s ease, margin 0.3s ease;
  margin-top: 0;
}

.help-text.show {
  max-height: 200px;
  opacity: 1;
  margin-top: 0.5rem;
}

.info-toggle {
  color: #6c757d;
  transition: color 0.2s;
}

.info-toggle:hover {
  color: #0d6efd;
}

/* Summary card styling */
.summary-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #dee2e6;
  border-radius: 12px;
}

.result-value {
  background: #212529;
  color: #fff;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-weight: 600;
}

/* Better focus states */
.form-control:focus,
.form-select:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* Input group styling */
.input-group-text {
  background-color: #e9ecef;
  font-weight: 500;
}

/* Button transitions */
.btn {
  transition: all 0.2s ease;
}

.btn-success {
  color: #fff !important;
}

/* Alert improvements */
.alert-success {
  background-color: #d1e7dd;
  border-color: #badbcc;
  color: #0f5132;
}

/* Mobile sticky results */
@media (max-width: 991.98px) {
  #printable {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 100;
    padding-top: 1rem;
    margin-top: 1rem;
    border-top: 2px solid #dee2e6;
  }
  
  .summary-card {
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }

  /* Fix language selector on mobile */
  .main-card .card-header .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 0.75rem;
  }

  .language-selector {
    width: 100%;
  }

  .language-selector .form-select {
    min-width: 120px;
  }
}

/* disable borders for print */
@media print {
  .no-print-border {
    border: none !important;
  }

  .summary-card .result-value {
    background-color: #fff !important;
    color: #000 !important;
    font-weight: bolder !important;
    border: 1px solid #000;
  }

  .form-section {
    background: transparent;
    border: none;
    padding: 0;
  }
  
  .progress-section {
    display: none;
  }
}
</style>
